import React, { createContext, useContext, useState } from 'react'
import { ChevronDown } from 'react-feather'
import styled from 'styled-components'

interface SelectProps {
  value?: string
  onValueChange?: (value: string) => void
  children?: React.ReactNode
  disabled?: boolean | null
}

interface SelectTriggerProps {
  children?: React.ReactNode
}

interface SelectItemProps {
  value: string
  children?: React.ReactNode
  disabled?: boolean | null
}

interface SelectContextType {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  value?: string
  onValueChange?: (value: string) => void
  disabled?: boolean | null
}

interface SelectValueProps {
  placeholder?: string
  children?: React.ReactNode
}

const baseStyles = {
  borderRadius: '0.5rem',
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
}

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`

const StyledTrigger = styled.button<{ $isOpen?: boolean }>`
  display: flex;
  height: 40px;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  border-radius: ${baseStyles.borderRadius};
  background: ${({ theme }) => theme.cardSecondary};
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  padding: 0 12px;
  font-size: ${baseStyles.fontSize};
  line-height: ${baseStyles.lineHeight};
  gap: 32px;
  transition: ${baseStyles.transition};
  box-sizing: border-box;
  position: relative;
  cursor: pointer;
  margin: 0;
  color: ${({ theme }) => theme.textPrimary};

  &:focus {
    outline: none;
    ring: 2px solid hsl(240 5% 84%);
    ring-offset: 2px;
  }

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    height: 16px;
    width: 16px;
    transform: ${(props) => (props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    transition: transform 150ms ease;
    flex-shrink: 0; /* Prevents icon from shrinking */
  }
`

const StyledContent = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  left: 0;
  width: 100%;
  z-index: 50;
  overflow: auto;
  border-radius: ${baseStyles.borderRadius};
  background: ${({ theme }) => theme.cardSecondary};
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  margin-top: 8px;
  max-height: 220px;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transform: ${(props) => (props.$isOpen ? 'translateY(0)' : 'translateY(-8px)')};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
`

const StyledItem = styled.button<{ $isSelected?: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  border: 0;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  align-items: center;
  padding: 8px 12px;
  font-size: ${baseStyles.fontSize};
  line-height: ${baseStyles.lineHeight};
  transition: ${baseStyles.transition};
  user-select: none;

  color: ${(props) => (props.$isSelected ? props.theme.textPrimary : props.theme.textSecondary)};
  background-color: ${(props) => (props.$isSelected ? props.theme.cardPrimary : 'transparent')};
  font-weight: ${(props) => (props.$isSelected ? '500' : 'normal')};

  &:not(:disabled):hover,
  &:focus-visible {
    background-color: ${({ theme }) => theme.cardSecondary};
    color: ${({ theme }) => theme.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accentAction};
    outline-offset: -2px;
  }

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const StyledValue = styled.span`
  display: flex;
  gap: 8px;
  align-items: center;

  > span {
    color: ${({ theme }) => theme.textSecondary};
  }
`

const SelectContext = createContext<SelectContextType | undefined>(undefined)

const useSelectContext = () => {
  const context = useContext(SelectContext)
  if (!context) {
    throw new Error('Select components must be used within a Select component')
  }
  return context
}

export const Select: React.FC<SelectProps> = ({ value, onValueChange, children, disabled }) => {
  const [isOpen, setIsOpen] = useState(disabled ? false : false)

  return (
    <SelectContext.Provider value={{ isOpen, setIsOpen, value, onValueChange, disabled }}>
      <SelectWrapper>{children}</SelectWrapper>
    </SelectContext.Provider>
  )
}

export const SelectTrigger: React.FC<SelectTriggerProps> = ({ children }) => {
  const { isOpen, setIsOpen, disabled } = useSelectContext()

  const handleClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  return (
    <StyledTrigger onClick={handleClick} $isOpen={isOpen} data-disabled={disabled}>
      {children}
      <ChevronDown />
    </StyledTrigger>
  )
}

export const SelectContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isOpen } = useSelectContext()

  return <StyledContent $isOpen={isOpen}>{children}</StyledContent>
}

export const SelectItem: React.FC<SelectItemProps> = ({ value, children, disabled }) => {
  const { onValueChange, setIsOpen } = useSelectContext()

  const handleClick = () => {
    if (!disabled && onValueChange) {
      onValueChange(value)
      setIsOpen(false)
    }
  }

  return (
    <StyledItem type="button" onClick={handleClick} disabled={Boolean(disabled)} data-disabled={disabled}>
      {children}
    </StyledItem>
  )
}

export const SelectValue: React.FC<SelectValueProps> = ({ children, placeholder }) => {
  const { value } = useSelectContext()

  return <StyledValue>{children || value || <span>{placeholder}</span>}</StyledValue>
}
